import { useCallback, useEffect, useMemo, useState } from 'react';
import Icon from './Icon';
import Modal from './Modal';
import { getAccessToken, isSuperAdminContextUser } from '../authStorage';
import { useLanguage } from '../i18n/language';
import {
  createSuperAdminMember,
  deactivateSuperAdminMember,
  fetchSuperAdminMemberCompanies,
  fetchSuperAdminMembers,
  updateSuperAdminMember,
} from '../superAdminMembersApi';
import './BranchesPage.css';
import './DecisionsPage.css';
import './Modal.css';
import './UsersPage.css';

const PAGE_SIZE = 10;
const emptyForm = () => ({
  companyId: '',
  name: '',
  position: '',
  phone: '',
  email: '',
  userName: '',
  password: '',
  isActive: true,
});

export default function SuperAdminMembersPage() {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = {
    title: isAr ? 'إدارة الأعضاء' : 'Members Management',
    subtitle: isAr ? 'إدارة أعضاء مجالس الإدارة على مستوى جميع الشركات.' : 'Manage board members across all companies.',
    deniedSub: isAr ? 'هذه الصفحة متاحة للسوبر أدمن فقط' : 'This page is available for Super Admin only',
    add: isAr ? 'إضافة عضو' : 'Add member',
    edit: isAr ? 'تعديل' : 'Edit',
    deactivate: isAr ? 'تعطيل' : 'Deactivate',
    active: isAr ? 'نشط' : 'Active',
    inactive: isAr ? 'غير نشط' : 'Inactive',
    hasAccount: isAr ? 'لديه حساب' : 'Has account',
    noAccount: isAr ? 'بدون حساب' : 'No account',
    total: isAr ? 'الإجمالي' : 'Total',
    searchPlaceholder: isAr ? 'بحث بالاسم، المنصب، الشركة، البريد، الجوال، اسم المستخدم...' : 'Search by name, position, company, email, phone, username...',
    allCompanies: isAr ? 'كل الشركات' : 'All companies',
    allStatuses: isAr ? 'كل الحالات' : 'All statuses',
    allAccounts: isAr ? 'كل أنواع الحسابات' : 'All account types',
    allPositions: isAr ? 'كل المناصب' : 'All positions',
    sortByName: isAr ? 'الاسم' : 'Name',
    sortByCompany: isAr ? 'الشركة' : 'Company',
    sortByPosition: isAr ? 'المنصب' : 'Position',
    sortByStatus: isAr ? 'الحالة' : 'Status',
    sortByAccount: isAr ? 'الحساب' : 'Account',
    sortById: isAr ? 'الرقم' : 'ID',
    asc: isAr ? 'تصاعدي' : 'Ascending',
    desc: isAr ? 'تنازلي' : 'Descending',
    resetFilters: isAr ? 'إعادة ضبط' : 'Reset',
    colName: isAr ? 'الاسم الكامل' : 'Full name',
    colPosition: isAr ? 'المنصب' : 'Position',
    colCompany: isAr ? 'الشركة' : 'Company',
    colStatus: isAr ? 'الحالة' : 'Status',
    loading: isAr ? 'جارٍ تحميل الأعضاء...' : 'Loading members...',
    noResults: isAr ? 'لا يوجد نتائج.' : 'No results.',
    showing: isAr ? 'عرض' : 'Showing',
    from: isAr ? 'من' : 'of',
    modalAddTitle: isAr ? 'إضافة عضو' : 'Add member',
    modalEditTitle: isAr ? 'تعديل عضو' : 'Edit member',
    company: isAr ? 'الشركة' : 'Company',
    name: isAr ? 'اسم العضو' : 'Member name',
    position: isAr ? 'المنصب' : 'Position',
    phone: isAr ? 'الجوال' : 'Phone',
    email: isAr ? 'البريد الإلكتروني' : 'Email',
    userName: isAr ? 'اسم المستخدم (اختياري)' : 'Username (optional)',
    password: isAr ? 'كلمة المرور' : 'Password',
    newPassword: isAr ? 'كلمة مرور جديدة (اختياري)' : 'New password (optional)',
    activeAccount: isAr ? 'العضو نشط' : 'Member is active',
    save: isAr ? 'حفظ' : 'Save',
    saveChanges: isAr ? 'حفظ التغييرات' : 'Save changes',
    adding: isAr ? 'جارٍ الإضافة...' : 'Adding...',
    saving: isAr ? 'جارٍ الحفظ...' : 'Saving...',
    cancel: isAr ? 'إلغاء' : 'Cancel',
    required: isAr ? 'الاسم والمنصب والشركة مطلوبة' : 'Name, position, and company are required',
    loadFailed: isAr ? 'تعذر تحميل البيانات' : 'Unable to load data',
    createFailed: isAr ? 'تعذر إضافة العضو' : 'Unable to add member',
    updateFailed: isAr ? 'تعذر تحديث العضو' : 'Unable to update member',
    deactivateFailed: isAr ? 'تعذر تعطيل العضو' : 'Unable to deactivate member',
    confirmDeactivate: isAr ? 'هل تريد تعطيل هذا العضو؟' : 'Do you want to deactivate this member?',
  };

  const [accessDenied, setAccessDenied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [positions, setPositions] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const [filters, setFilters] = useState({
    q: '',
    companyId: '',
    isActive: 'all',
    hasAccount: 'all',
    position: '',
    sortBy: 'name',
    sortDir: 'asc',
  });

  const loadData = useCallback(async (page = 1, nextFilters = filters) => {
    setLoading(true);
    setError('');
    try {
      const [listRes, companyRes] = await Promise.all([
        fetchSuperAdminMembers({ page, pageSize: PAGE_SIZE, ...nextFilters }),
        fetchSuperAdminMemberCompanies(),
      ]);
      setRows(listRes.items);
      setPositions(listRes.positions);
      setCompanies(companyRes);
      setCurrentPage(listRes.page);
      setTotalRows(listRes.total);
      setTotalPages(listRes.totalPages);
    } catch (e) {
      setError(e instanceof Error ? e.message : t.loadFailed);
    } finally {
      setLoading(false);
    }
  }, [filters, t.loadFailed]);

  useEffect(() => {
    if (!getAccessToken()) {
      window.location.assign('/login');
      return;
    }
    if (!isSuperAdminContextUser()) {
      setAccessDenied(true);
      setLoading(false);
      return;
    }
    loadData(1, filters);
  }, [loadData, filters]);

  const stats = useMemo(() => ({
    active: rows.filter((x) => x.isActive).length,
    withAccount: rows.filter((x) => x.hasAccount).length,
  }), [rows]);

  const updateFilter = (key, value) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    loadData(1, next);
  };

  const resetFilters = () => {
    const next = { q: '', companyId: '', isActive: 'all', hasAccount: 'all', position: '', sortBy: 'name', sortDir: 'asc' };
    setFilters(next);
    loadData(1, next);
  };

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm());
    setError('');
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditId(item.id);
    setForm({
      companyId: String(item.companyId),
      name: item.name,
      position: item.position,
      phone: item.phone || '',
      email: item.email || '',
      userName: item.userName || '',
      password: '',
      isActive: item.isActive,
    });
    setError('');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditId(null);
    setForm(emptyForm());
  };

  const submitForm = async (e) => {
    e.preventDefault();
    if (!form.companyId || !form.name.trim() || !form.position.trim()) {
      setError(t.required);
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      if (editId) {
        await updateSuperAdminMember(editId, {
          companyId: Number(form.companyId),
          name: form.name.trim(),
          position: form.position.trim(),
          phone: form.phone.trim() || undefined,
          email: form.email.trim() || undefined,
          userName: form.userName.trim() || undefined,
          newPassword: form.password.trim() || undefined,
          isActive: form.isActive,
        });
      } else {
        await createSuperAdminMember({
          companyId: Number(form.companyId),
          name: form.name.trim(),
          position: form.position.trim(),
          phone: form.phone.trim() || undefined,
          email: form.email.trim() || undefined,
          userName: form.userName.trim() || undefined,
          password: form.password.trim() || undefined,
          isActive: form.isActive,
        });
      }
      closeModal();
      await loadData(currentPage, filters);
    } catch (e) {
      setError(e instanceof Error ? e.message : (editId ? t.updateFailed : t.createFailed));
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (item) => {
    if (togglingId) return;
    setTogglingId(item.id);
    try {
      await updateSuperAdminMember(item.id, {
        companyId: Number(item.companyId),
        name: item.name,
        position: item.position,
        phone: item.phone || undefined,
        email: item.email || undefined,
        userName: item.userName || undefined,
        isActive: !item.isActive,
      });
      setRows((prev) => prev.map((r) => r.id === item.id ? { ...r, isActive: !item.isActive } : r));
    } catch (e) {
      setError(e instanceof Error ? e.message : t.updateFailed);
    } finally {
      setTogglingId(null);
    }
  };

  const goToPage = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    loadData(page, filters);
  };

  if (accessDenied) {
    return (
      <div className="page">
        <div className="page__topbar">
          <div className="page__topbar-left">
            <span className="page__topbar-title">{t.title}</span>
            <span className="page__topbar-sub">{t.deniedSub}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page users-page">
      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editId ? t.modalEditTitle : t.modalAddTitle}
        subtitle={t.subtitle}
        icon={<Icon name="board" size={18} color="#fff" />}
        size="lg"
        variant="drawer-left"
        headerActions={(
          <button type="submit" form="member-superadmin-form" className="btn btn--primary" disabled={submitting}>
            {submitting ? (editId ? t.saving : t.adding) : (editId ? t.saveChanges : t.save)}
          </button>
        )}
        footer={<button type="button" className="btn btn--ghost" disabled={submitting} onClick={closeModal}>{t.cancel}</button>}
      >
        <form id="member-superadmin-form" className="mform" onSubmit={submitForm}>
          {error && <div className="mform__error" style={{ padding: '10px 12px', background: '#ffebee', borderRadius: 10 }}>{error}</div>}
          <div className="mform__row">
            <div className="mform__field">
              <label className="mform__label">{t.company} <span>*</span></label>
              <select className="mform__select" value={form.companyId} onChange={(e) => setForm((p) => ({ ...p, companyId: e.target.value }))}>
                <option value="">{isAr ? 'اختر الشركة' : 'Select company'}</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="mform__field">
              <label className="mform__label">{t.name} <span>*</span></label>
              <input className="mform__input" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
            </div>
          </div>
          <div className="mform__row">
            <div className="mform__field">
              <label className="mform__label">{t.position} <span>*</span></label>
              <input className="mform__input" value={form.position} onChange={(e) => setForm((p) => ({ ...p, position: e.target.value }))} />
            </div>
            <div className="mform__field">
              <label className="mform__label">{t.phone}</label>
              <input className="mform__input" value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} />
            </div>
          </div>
          <div className="mform__row">
            <div className="mform__field">
              <label className="mform__label">{t.email}</label>
              <input className="mform__input" type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
            </div>
            <div className="mform__field">
              <label className="mform__label">{t.userName}</label>
              <input className="mform__input" value={form.userName} onChange={(e) => setForm((p) => ({ ...p, userName: e.target.value }))} />
            </div>
          </div>
          <div className="mform__row">
            <div className="mform__field">
              <label className="mform__label">{editId ? t.newPassword : t.password}</label>
              <input className="mform__input" type="password" value={form.password} onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))} />
            </div>
            <div className="mform__field" style={{ display: 'flex', alignItems: 'flex-end' }}>
              <label className="mform__label" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.isActive} onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))} />
                {t.activeAccount}
              </label>
            </div>
          </div>
        </form>
      </Modal>

      <div className="list-page-card list-page-card--unified">
        <header className="list-page-card__header">
          <div className="list-page-card__titles">
            <span className="list-page-card__title">
              <Icon name="board" size={18} color="#00334a" style={{ verticalAlign: 'middle', marginLeft: 6 }} />
              {t.title}
            </span>
            <span className="list-page-card__sub">{t.subtitle}</span>
          </div>
          <div className="list-page-card__actions">
            <button type="button" className="btn btn--primary" onClick={openCreate}>
              <Icon name="plus" size={14} color="#fff" />
              {t.add}
            </button>
          </div>
        </header>

        <div className="page__stats">
        <div className="page__stat-chip page__stat-chip--total"><Icon name="board" size={13} /> {t.total}: {totalRows}</div>
        <div className="page__stat-chip page__stat-chip--active"><Icon name="check-circle" size={13} /> {t.active}: {stats.active}</div>
        <div className="page__stat-chip page__stat-chip--new"><Icon name="user" size={13} /> {t.hasAccount}: {stats.withAccount}</div>
        </div>

        <div className="page__filters" style={{ flexWrap: 'wrap', gap: 10 }}>
        <div className="page__search" style={{ minWidth: 280, flex: '1 1 320px' }}>
          <Icon name="search" size={14} color="#90a4ae" />
          <input className="page__search-input" value={filters.q} onChange={(e) => updateFilter('q', e.target.value)} placeholder={t.searchPlaceholder} />
        </div>
        <select className="page__filter-select" value={filters.companyId} onChange={(e) => updateFilter('companyId', e.target.value)}>
          <option value="">{t.allCompanies}</option>
          {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select className="page__filter-select" value={filters.isActive} onChange={(e) => updateFilter('isActive', e.target.value)}>
          <option value="all">{t.allStatuses}</option>
          <option value="active">{t.active}</option>
          <option value="inactive">{t.inactive}</option>
        </select>
        <select className="page__filter-select" value={filters.hasAccount} onChange={(e) => updateFilter('hasAccount', e.target.value)}>
          <option value="all">{t.allAccounts}</option>
          <option value="yes">{t.hasAccount}</option>
          <option value="no">{t.noAccount}</option>
        </select>
        <select className="page__filter-select" value={filters.position} onChange={(e) => updateFilter('position', e.target.value)}>
          <option value="">{t.allPositions}</option>
          {positions.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select className="page__filter-select" value={filters.sortBy} onChange={(e) => updateFilter('sortBy', e.target.value)}>
          <option value="name">{t.sortByName}</option>
          <option value="company">{t.sortByCompany}</option>
          <option value="position">{t.sortByPosition}</option>
          <option value="status">{t.sortByStatus}</option>
          <option value="hasAccount">{t.sortByAccount}</option>
          <option value="id">{t.sortById}</option>
        </select>
        <select className="page__filter-select" value={filters.sortDir} onChange={(e) => updateFilter('sortDir', e.target.value)}>
          <option value="asc">{t.asc}</option>
          <option value="desc">{t.desc}</option>
        </select>
        <button type="button" className="btn btn--ghost" onClick={resetFilters}>{t.resetFilters}</button>
        </div>

        {error && !modalOpen && <div className="list-page-card__banner list-page-card__banner--error">{error}</div>}
        {loading && <div className="list-page-card__loading">{t.loading}</div>}

        {!loading && (
        <div className="table-card">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 42 }}>#</th>
                <th>{t.colName}</th>
                <th>{t.colPosition}</th>
                <th>{t.colCompany}</th>
                <th>{t.colStatus}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((item, idx) => (
                <tr key={item.id} style={{ cursor: 'pointer' }} onClick={() => openEdit(item)}>
                  <td className="td-num">{(currentPage - 1) * PAGE_SIZE + idx + 1}</td>
                  <td><strong>{item.name || '—'}</strong></td>
                  <td>{item.position || '—'}</td>
                  <td>{item.companyName || '—'}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <button
                        type="button"
                        className={`sa-toggle${item.isActive ? ' sa-toggle--on' : ''}`}
                        disabled={togglingId === item.id}
                        onClick={(e) => { e.stopPropagation(); toggleActive(item); }}
                        title={item.isActive ? t.active : t.inactive}
                      >
                        <span className="sa-toggle__thumb" />
                      </button>
                      <span style={{ fontSize: '0.78rem', fontWeight: 700, color: item.isActive ? '#02c3a6' : '#90a4ae' }}>
                        {item.isActive ? t.active : t.inactive}
                      </span>
                      <button
                        type="button"
                        className="btn btn--ghost"
                        style={{ padding: '4px 10px', fontSize: '0.76rem' }}
                        onClick={(e) => { e.stopPropagation(); openEdit(item); }}
                      >
                        <Icon name="edit" size={12} /> {t.edit}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && <div className="users-empty">{t.noResults}</div>}
          {totalPages > 1 && (
            <div className="pagination">
              <span>
                {t.showing} {Math.min(totalRows, (currentPage - 1) * PAGE_SIZE + 1)}-{Math.min(totalRows, currentPage * PAGE_SIZE)} {t.from} {totalRows}
              </span>
              <div className="pagination__pages">
                <button type="button" className="pagination__btn" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>‹</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button key={p} type="button" className={`pagination__btn${currentPage === p ? ' pagination__btn--active' : ''}`} onClick={() => goToPage(p)}>{p}</button>
                ))}
                <button type="button" className="pagination__btn" onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>›</button>
              </div>
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
}
